import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ThemeProvider } from "@material-ui/core/styles";
import { globalTheme } from "style/globalTheme";
import styled from "styled-components";
import { brandPalette } from "brand/theme/brandTheme";
import { Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, CircularProgress, Box, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FormWidget from "app/components/mvp/formWidget";
import ListWidget from "app/components/mvp/listWidget";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { formatData } from "utilities/datatable";
import { config } from "utilities/config";
import { post } from "app/services/api";

const Heading = styled.div`
  height: 15rem;
  padding: 1rem 1.2rem;
  background-image: linear-gradient(
      to right bottom,
      ${brandPalette["Complementary"][200]},
      ${brandPalette["Analogous-1"][600]}
    ),
    url(brand/slide4.jpeg);
  clip-path: polygon(0 0, 100% 0, 100% 80%, 0% 100%);
  display: flex;
  align-items: flex-end;

  h2 {
    font-size: 2rem;
    letter-spacing: 0.07rem;
    text-transform: uppercase;
    font-weight: 700;
    color: white;
    background-image: linear-gradient(
      to right,
      rgba(60, 60, 60, 0.5),
      rgba(0, 28, 48, 0.5)
    );
    padding: 0.5rem 1.5rem;
    border-radius: 8px;
    margin: 0 0 1.5rem 0;
  }
`;

const Content = styled.section`
  padding: 2rem 1rem;
  min-height: 30rem;
  background-color: #fafafa;
`;

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    marginTop: theme.spacing(3),
    boxShadow: theme.shadows[2],
  },
  table: {
    minWidth: 650,
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    "& .MuiTableCell-head": {
      color: theme.palette.common.white,
      fontWeight: 600,
    },
  },
  actionButton: {
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    textTransform: "none",
    minWidth: "auto",
    padding: theme.spacing(0.5, 1),
  },
  actionCell: {
    whiteSpace: "nowrap",
    padding: theme.spacing(1),
  },
  statusPending: {
    color: theme.palette.warning.main,
    fontWeight: 600,
  },
  statusApproved: {
    color: theme.palette.success.main,
    fontWeight: 600,
  },
  statusRejected: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  statusMatched: {
    color: theme.palette.info.main,
    fontWeight: 600,
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    maxWidth: "90vw",
    maxHeight: "90vh",
    overflow: "auto",
    position: "relative",
  },
  modalClose: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  modalContent: {
    marginTop: theme.spacing(2),
  },
}));

const MentoringReviewRequests = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const contactid = useSelector((state) => state.contactid);
  const [menteeMatches, setMenteeMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchDetails, setMatchDetails] = useState({});
  const [topicsData, setTopicsData] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [formWidgetRef, setFormWidgetRef] = useState(null);
  // Use ref instead of state to prevent re-renders that cause scroll issues
  const currentFormDataRef = useRef({});
  const dialogContentRef = useRef(null);

  // Memoize updateFormData - use ref to avoid re-renders
  const handleUpdateFormData = useCallback((entityType, formData) => {
    // Store form data in ref (doesn't cause re-render)
    currentFormDataRef.current = formData;
  }, []);

  const fetchMenteeMatches = useCallback(() => {
    setLoading(true);
    const api = `${config.teleportal.env}${config.teleportal.submitForm.get}`;
    const payload = {
      recordtype: "ctpt_mentormenteematcheses",
      idfield: "ctpt_mentormenteematchesid",
      properties: [
        "ctpt_approvalstatus",
        "ctpt_availablitymatches",
        "ctpt_mentor",
        "ctpt_mentee",
        "ctpt_mentormenteematchesid",
        "ctpt_name",
        "ctpt_topicsmatched",
        "createdon",
        "ctpt_completionstatus",
        "ctpt_hourslogged",
        "ctpt_statusnotes"
      ],
      formattedFields: [
        "_ctpt_mentor_value",
        "_ctpt_mentee_value"
      ],
      filter: {
        globalfilter: "",
        globalAndOperator: false,
        fieldAndOperator: false,
        filterfields: [
          {
            field: "_ctpt_mentor_value",
            searchvalue: contactid,
            type: "guid"
          }
        ]
      },
      continuationToken: "",
      pazesize: "1000"
    };

    axios.post(api, payload, {
      headers: {
        "Authorization": "Bearer " + localStorage.getItem("token"),
        "Content-Type": "application/json"
      }
    })
      .then((res) => {
        console.log("Full API Response:", JSON.stringify(res.data, null, 2)); // Debug log
        try {
          // Handle both lowercase 'data' and uppercase 'Data' for compatibility
          const responseData = res.data?.data || res.data?.Data;
          
          if (!res.data) {
            console.error("No data in response");
            setMenteeMatches([]);
            setLoading(false);
            return;
          }
          
          if (!responseData || !Array.isArray(responseData)) {
            console.error("Response data is not an array:", responseData);
            console.log("Response structure:", {
              hasData: !!res.data,
              responseData: responseData,
              type: typeof responseData,
              isArray: Array.isArray(responseData)
            });
            setMenteeMatches([]);
            setLoading(false);
            return;
          }
          
          // Extract records from the nested structure
          const extractedRecords = [];
          
          responseData.forEach((outerItem, outerIndex) => {
            console.log(`Processing outer item ${outerIndex}:`, outerItem);
            
            if (outerItem && outerItem.data && Array.isArray(outerItem.data)) {
              // Check if the data array contains field objects (has 'name' property) or nested items
              const firstItem = outerItem.data[0];
              
              if (firstItem && firstItem.name !== undefined) {
                // Format: { id: "...", data: [{name: "field", value: "value"}, ...] }
                extractedRecords.push({
                  id: outerItem.id || outerItem.ctpt_mentormenteematchesid || `record-${outerIndex}`,
                  data: outerItem.data
                });
              } else {
                // Nested structure - flatten it
                outerItem.data.forEach((nestedItem) => {
                  if (nestedItem && nestedItem.data && Array.isArray(nestedItem.data)) {
                    extractedRecords.push({
                      id: nestedItem.id || nestedItem.ctpt_mentormenteematchesid || `record-${extractedRecords.length}`,
                      data: nestedItem.data
                    });
                  } else if (nestedItem) {
                    // Direct record format
                    extractedRecords.push({
                      id: nestedItem.id || nestedItem.ctpt_mentormenteematchesid || `record-${extractedRecords.length}`,
                      data: Object.keys(nestedItem).map(key => ({
                        name: key,
                        value: nestedItem[key]
                      }))
                    });
                  }
                });
              }
            } else if (outerItem) {
              // Direct record format - convert to name/value pairs
              extractedRecords.push({
                id: outerItem.id || outerItem.ctpt_mentormenteematchesid || `record-${outerIndex}`,
                data: Object.keys(outerItem).map(key => ({
                  name: key,
                  value: outerItem[key]
                }))
              });
            }
          });
          
          if (extractedRecords.length > 0) {
            // Format the extracted records
            const wrappedResponse = { data: extractedRecords };
            const formattedRequests = formatData(wrappedResponse);
            console.log("Formatted Requests:", formattedRequests);
            console.log("Formatted Requests count:", formattedRequests.length);
            
            // Keep all non-rejected records (requested behavior)
            const filteredRequests = formattedRequests.filter((match) => {
              return !isRejectedApprovalStatus(match);
            });
            
            console.log("Filtered Requests:", filteredRequests);
            console.log("Filtered Requests count:", filteredRequests.length);
            
            if (filteredRequests && filteredRequests.length > 0) {
              setMenteeMatches(filteredRequests);
            } else {
              console.warn("No records found after filtering");
              setMenteeMatches([]);
            }
          } else {
            console.warn("No records extracted from response");
            setMenteeMatches([]);
          }
        } catch (error) {
          console.error("Error processing response:", error);
          setMenteeMatches([]);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log("Error fetching mentee matches:", error);
        dispatch({
          type: "SET_STATUS",
          payload: {
            message: "Failed to load mentee matches",
            code: "",
            display: "show",
            severity: "error",
          },
        });
        setMenteeMatches([]);
        setLoading(false);
      });
  }, [dispatch, contactid]);

  useEffect(() => {
    fetchMenteeMatches();
  }, [fetchMenteeMatches]);

  const getStatusClass = (status) => {
    const statusText = String(status ?? "").toLowerCase();
    if (statusText.includes("approve")) {
      return classes.statusApproved;
    }
    if (statusText.includes("reject")) {
      return classes.statusRejected;
    }
    if (statusText.includes("match")) {
      return classes.statusMatched;
    }
    return classes.statusPending;
  };

  const getODataFormattedValue = (record, fieldLogicalName) => {
    if (!record || !fieldLogicalName) return undefined;
    const k = `${fieldLogicalName}@OData.Community.Display.V1.FormattedValue`;
    return record[k];
  };

  const getLookupDisplayName = (record, fieldLogicalName) => {
    // Possible shapes seen across the app + Dataverse:
    // - record[field] is a string
    // - record[field] is { value: "..." }
    // - record[`_${field}_value@...FormattedValue`] is "..."
    // - record[`_${field}_value`] is "..." (sometimes already display name)
    if (!record || !fieldLogicalName) return "N/A";

    const direct = record[fieldLogicalName];
    if (direct && typeof direct === "object" && direct.value !== undefined && direct.value !== null) {
      return String(direct.value);
    }
    if (typeof direct === "string" && direct.trim()) {
      return direct;
    }

    const formatted = getODataFormattedValue(record, `_${fieldLogicalName}_value`);
    if (formatted) return formatted;

    const underscoreVal = record[`_${fieldLogicalName}_value`];
    if (underscoreVal !== undefined && underscoreVal !== null && String(underscoreVal).trim()) {
      return String(underscoreVal);
    }

    return "N/A";
  };

  const getLookupGuid = (record, fieldLogicalName) => {
    // Extract the actual GUID value, not the display name
    // The raw field (e.g., ctpt_mentee) should contain the GUID before formatting
    // The formatted field (_ctpt_mentee_value) contains the display name after formatData
    if (!record || !fieldLogicalName) return null;

    const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    // First, check the raw field name (e.g., ctpt_mentee) - this should have the GUID
    const rawField = record[fieldLogicalName];
    if (rawField) {
      if (typeof rawField === "string" && rawField.match(guidPattern)) {
        return rawField;
      }
      if (typeof rawField === "object" && rawField.value && typeof rawField.value === "string" && rawField.value.match(guidPattern)) {
        return rawField.value;
      }
      if (typeof rawField === "object" && rawField.id && typeof rawField.id === "string" && rawField.id.match(guidPattern)) {
        return rawField.id;
      }
    }

    // Check the underscore field - might be GUID if not formatted, or display name if formatted
    const underscoreField = `_${fieldLogicalName}_value`;
    const underscoreVal = record[underscoreField];
    if (underscoreVal) {
      const valStr = String(underscoreVal);
      if (valStr.match(guidPattern)) {
        return valStr;
      }
    }

    // Debug: log what we found to help diagnose
    console.log(`getLookupGuid for ${fieldLogicalName}:`, {
      rawField,
      underscoreField,
      underscoreVal,
      allRelevantKeys: Object.keys(record).filter(k => 
        k.toLowerCase().includes(fieldLogicalName.toLowerCase()) || 
        k.toLowerCase().includes('mentee') || 
        k.toLowerCase().includes('mentor')
      )
    });

    return null;
  };

  const getChoiceLabel = (record, fieldLogicalName) => {
    if (!record || !fieldLogicalName) return "";
    const formatted = getODataFormattedValue(record, fieldLogicalName);
    if (formatted) return formatted;
    const raw = record[fieldLogicalName];
    if (raw === undefined || raw === null) return "";
    return String(raw);
  };

  const formatYesNo = (value) => {
    if (value === true || value === "true" || value === 1 || value === "1") return "Yes";
    if (value === false || value === "false" || value === 0 || value === "0") return "No";
    return value === undefined || value === null ? "" : String(value);
  };

  const formatApprovalStatus = (record, fieldLogicalName) => {
    if (!record || !fieldLogicalName) return "Pending";
    
    // First try to get the formatted value (OData formatted value)
    const formatted = getODataFormattedValue(record, fieldLogicalName);
    if (formatted) {
      // If formatted value exists, check if it's already text or numeric
      const formattedLower = String(formatted).toLowerCase();
      if (formattedLower.includes("approve")) return "Approved";
      if (formattedLower.includes("reject")) return "Rejected";
      if (formattedLower.includes("pending")) return "Pending";
    }
    
    // Get the raw value
    const raw = record[fieldLogicalName];
    if (raw === undefined || raw === null) return "Pending";
    
    // Convert to string and check numeric values
    const rawStr = String(raw);
    const rawNum = Number(raw);
    
    // Map numeric option set values to text labels
    if (rawNum === 533910000 || rawStr === "533910000") return "Approved";
    if (rawNum === 533910001 || rawStr === "533910001" || rawNum === 53910001 || rawStr === "53910001") return "Rejected";
    if (rawNum === 727530001 || rawStr === "727530001") return "Approved"; // Alternative option set value
    if (rawNum === 727530002 || rawStr === "727530002") return "Rejected"; // Alternative option set value
    
    // If it's already a string, check for keywords
    const rawLower = rawStr.toLowerCase();
    if (rawLower.includes("approve")) return "Approved";
    if (rawLower.includes("reject")) return "Rejected";
    if (rawLower.includes("pending")) return "Pending";
    
    // Default fallback
    return rawStr || "Pending";
  };

  const isRejectedApprovalStatus = (record) => {
    const status = formatApprovalStatus(record, "ctpt_approvalstatus");
    return String(status || "").toLowerCase().includes("reject");
  };

  const isPendingApprovalStatus = (record) => {
    const status = formatApprovalStatus(record, "ctpt_approvalstatus");
    const normalizedStatus = String(status || "").toLowerCase();
    return normalizedStatus.includes("pending") || normalizedStatus === "";
  };

  const getApprovedYesNo = (record) => {
    const status = formatApprovalStatus(record, "ctpt_approvalstatus");
    return String(status || "").toLowerCase().includes("approve") ? "Yes" : "No";
  };

  const formatAvailabilityMatches = (record, fieldLogicalName) => {
    if (!record || !fieldLogicalName) return "N/A";
    
    // Mapping of numeric values to text labels
    const availabilityMap = {
      533910000: "Available on Weekends",
      533910001: "Available on Weekdays",
      533910002: "Available Daytime",
      533910003: "Available Evenings"
    };
    
    // Helper function to convert a single numeric value to text
    const mapValueToText = (value) => {
      if (value === undefined || value === null) return null;
      
      // Remove commas from number strings (e.g., "533,910,000" -> "533910000")
      const cleanValue = String(value).replace(/,/g, "").trim();
      if (!cleanValue) return null;
      
      const numValue = Number(cleanValue);
      
      // Check if it's a known mapping
      if (availabilityMap[numValue] !== undefined) {
        return availabilityMap[numValue];
      }
      
      // Check if it's already a text label
      const valueStr = String(value).trim();
      if (valueStr.includes("Weekends") || valueStr.includes("Weekdays") || 
          valueStr.includes("Daytime") || valueStr.includes("Evenings")) {
        return valueStr;
      }
      
      return null;
    };
    
    // First try to get the formatted value (OData formatted value)
    const formatted = getODataFormattedValue(record, fieldLogicalName);
    if (formatted) {
      const formattedStr = String(formatted).trim();
      // If formatted value contains our labels, return it (might already be comma-separated)
      if (formattedStr.includes("Weekends") || formattedStr.includes("Weekdays") || 
          formattedStr.includes("Daytime") || formattedStr.includes("Evenings")) {
        return formattedStr;
      }
    }
    
    // Get the raw value
    const raw = record[fieldLogicalName];
    if (raw === undefined || raw === null) return "N/A";
    
    // Handle array of values
    if (Array.isArray(raw)) {
      const textValues = raw.map(mapValueToText).filter(v => v !== null);
      return textValues.length > 0 ? textValues.join(", ") : "N/A";
    }
    
    // Convert to string
    const rawStr = String(raw).trim();
    if (!rawStr) return "N/A";
    
    // Check if it's already text (contains our labels)
    if (rawStr.includes("Weekends") || rawStr.includes("Weekdays") || 
        rawStr.includes("Daytime") || rawStr.includes("Evenings")) {
      return rawStr;
    }
    
    // Remove all commas first to handle cases like "533,910,000,533,910,001"
    const noCommas = rawStr.replace(/,/g, "");
    
    // Extract all 9-digit numbers from the string
    // Pattern: 9 consecutive digits
    const numberPattern = /(\d{9})/g;
    const matches = noCommas.match(numberPattern);
    
    if (matches && matches.length > 0) {
      // Map each numeric value to its text label
      const textValues = matches
        .map(match => {
          const numValue = Number(match);
          return availabilityMap[numValue];
        })
        .filter(v => v !== undefined);
      
      return textValues.length > 0 ? textValues.join(", ") : "N/A";
    }
    
    // Try single value mapping
    const mapped = mapValueToText(rawStr);
    if (mapped) return mapped;
    
    // Default fallback
    return "N/A";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  const formatDateForDisplay = (record) => {
    const createdOn = record.createdon || record["createdon@OData.Community.Display.V1.FormattedValue"];
    return formatDate(createdOn);
  };

  const getMentorName = (match) => getLookupDisplayName(match, "ctpt_mentor");
  const getMenteeName = (match) => getLookupDisplayName(match, "ctpt_mentee");

  const handleApprove = useCallback((match) => {
    const matchId = match.ctpt_mentormenteematchesid || match.id;
    if (!matchId) {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Cannot approve: Missing record ID",
          code: "",
          display: "show",
          severity: "error",
        },
      });
      return;
    }

    const api = config.teleportal.submitForm.Update;

    // Payload for Updating the Approval Status to either Approved or Rejected
    const payload = {
      id: matchId,
      recordtype: "ctpt_mentormenteematcheses",
      data: [
        {
          name: "ctpt_approvalstatus",
          value: 533910000, // Approved option set value - adjust if needed based on your schema
          type: "optionsetvalue",
        },
      ],
    };

    console.log("Approving match with payload:", payload);

    post(api, payload)
      .then((res) => {
        console.log("Approve response:", res.data);
        // Check if the update was successful - API might return id or just success
        if (res.data && (res.data.id || res.data === matchId || res.status === 200 || res.status === 204)) {
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Match approved successfully",
              code: "",
              display: "show",
              severity: "success",
            },
          });
          // Refresh the data
          fetchMenteeMatches();
        } else {
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Failed to approve match",
              code: "",
              display: "show",
              severity: "error",
            },
          });
        }
      })
      .catch((error) => {
        console.error("Error approving match:", error);
        dispatch({
          type: "SET_STATUS",
          payload: {
            message: error.response?.data?.message || "Error approving match. Please try again.",
            code: "",
            display: "show",
            severity: "error",
          },
        });
      });
  }, [dispatch, fetchMenteeMatches]);

  const handleReject = useCallback((match) => {
    const matchId = match.ctpt_mentormenteematchesid || match.id;
    if (!matchId) {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Cannot reject: Missing record ID",
          code: "",
          display: "show",
          severity: "error",
        },
      });
      return;
    }

    const api = config.teleportal.submitForm.Update;
    // Try to use the raw value format if available, otherwise use option set value
    // Note: Option set values may need to be adjusted based on your Dataverse schema
    const payload = {
      id: matchId,
      recordtype: "ctpt_mentormenteematcheses",
      data: [
        {
          name: "ctpt_approvalstatus",
          value: 533910001, // Rejected option set value - adjust if needed based on your schema
          type: "optionsetvalue",
        },
      ],
    };

    console.log("Rejecting match with payload:", payload);

    post(api, payload)
      .then((res) => {
        console.log("Reject response:", res.data);
        // Check if the update was successful - API might return id or just success
        if (res.data && (res.data.id || res.data === matchId || res.status === 200 || res.status === 204)) {
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Match rejected successfully",
              code: "",
              display: "show",
              severity: "success",
            },
          });
          // Refresh the data
          fetchMenteeMatches();
        } else {
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Failed to reject match",
              code: "",
              display: "show",
              severity: "error",
            },
          });
        }
      })
      .catch((error) => {
        console.error("Error rejecting match:", error);
        dispatch({
          type: "SET_STATUS",
          payload: {
            message: error.response?.data?.message || "Error rejecting match. Please try again.",
            code: "",
            display: "show",
            severity: "error",
          },
        });
      });
  }, [dispatch, fetchMenteeMatches]);

  const handleView = useCallback((match) => {
    const matchId = match.ctpt_mentormenteematchesid || match.id;
    if (!matchId) {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Cannot view: Missing record ID",
          code: "",
          display: "show",
          severity: "error",
        },
      });
      return;
    }

    setSelectedMatch(match);
    setViewModalOpen(true);
    setLoadingDetails(true);
    setMatchDetails({});
    setTopicsData([]);

    // Use existing match data we already have - format it properly
    const menteeName = getMenteeName(match);
    const approvalStatusRaw = formatApprovalStatus(match, "ctpt_approvalstatus");
    // Convert approval status to dropdown format: "pending", "approved", or "rejected"
    let approvalStatus = "pending"; // Default to pending
    if (approvalStatusRaw) {
      const statusLower = approvalStatusRaw.toLowerCase();
      if (statusLower.includes("approve")) {
        approvalStatus = "approved";
      } else if (statusLower.includes("reject")) {
        approvalStatus = "rejected";
      } else if (statusLower.includes("pending")) {
        approvalStatus = "pending";
      }
    }
    const availabilityMatches = formatAvailabilityMatches(match, "ctpt_availablitymatches");
    const topicsMatched = formatYesNo(match.ctpt_topicsmatched) === "Yes";
    
    // Get the new fields from match data
    // Completion status option set values: 533910000 = Not Started, 533910001 = In Progress, 533910002 = Complete
    const completionStatusRaw = match.ctpt_completionstatus || 
                                getODataFormattedValue(match, "ctpt_completionstatus") || "";
    
    // Map completion status to option set values
    let completionStatus = "533910000"; // Default to Not Started
    if (completionStatusRaw) {
      const statusLower = String(completionStatusRaw).toLowerCase();
      if (statusLower.includes("progress") || statusLower === "533910001") {
        completionStatus = "533910001"; // In Progress
      } else if (statusLower.includes("complete") || statusLower === "533910002") {
        completionStatus = "533910002"; // Complete
      } else if (statusLower.includes("not started") || statusLower === "533910000") {
        completionStatus = "533910000"; // Not Started
      }
    }
    
    const hoursLogged = match.ctpt_hourslogged !== undefined && match.ctpt_hourslogged !== null 
                        ? Number(match.ctpt_hourslogged) 
                        : 0;
    const statusNotes = match.ctpt_statusnotes || "";
    
    const formData = {
      ctpt_mentee: menteeName,
      ctpt_approvalstatus: approvalStatus,
      ctpt_availablitymatches: availabilityMatches,
      ctpt_topicsmatched: topicsMatched,
      ctpt_completionstatus: completionStatus,
      ctpt_hourslogged: hoursLogged,
      ctpt_statusnotes: statusNotes,
    };
    
    console.log("Setting match details:", formData);
    console.log("Original match data:", match);
    
    // Set initial data immediately
    setMatchDetails(formData);
    currentFormDataRef.current = formData; // Initialize current form data ref
    setLoadingDetails(false);

    // Fetch topics for the match using MenteesMatchesId
    if (matchId && matchId.length > 0) {
      const topicsApi = config.teleportal.mentoring.listTopicsFromMentees;
      const topicsPayload = {
        MenteesMatchesId: matchId,
      };

      console.log("Calling topics API with payload:", topicsPayload);
      console.log("Using MenteesMatchesId:", matchId);

      post(topicsApi, topicsPayload)
        .then((res) => {
          console.log("Topics API response:", res.data);
          if (Array.isArray(res.data) && res.data.length > 0) {
            console.log("Raw topics data sample (first item):", res.data[0]);
            console.log("All topic item keys:", res.data.length > 0 ? Object.keys(res.data[0]) : []);
            
            const dataWithIds = res.data.map((item, index) => {
              // Log each item to see its structure
              if (index === 0) {
                console.log("Processing first topic item:", item);
                console.log("First item keys:", Object.keys(item));
              }
              
              const topicItem = {
                ...item,
                id: item.id || item.ctpt_topicsid || `topic-${index}`,
                // Ensure topicName and description fields exist - check all possible field names
                topicName: item.topicName || item.ctpt_topic || item.name || item["ctpt_topic@OData.Community.Display.V1.FormattedValue"] || "",
                description: item.description || item.ctpt_description || item["ctpt_description@OData.Community.Display.V1.FormattedValue"] || "",
              };
              
              if (index === 0) {
                console.log("Formatted first topic item:", topicItem);
              }
              
              return topicItem;
            });
            console.log("Formatted topics data (full):", JSON.stringify(dataWithIds, null, 2));
            setTopicsData(dataWithIds);
          } else {
            console.log("Topics response is not an array or is empty:", res.data);
            setTopicsData([]);
          }
        })
        .catch((error) => {
          console.error("Error fetching topics:", error);
          console.error("Error response:", error.response);
          console.error("Error details:", error.response?.data);
          console.error("Request config:", error.config);
          // Don't show error to user, just log it and show empty list
          setTopicsData([]);
        });
    } else {
      console.log("No valid MenteesMatchesId available for fetching topics");
      console.log("Match ID:", matchId);
      setTopicsData([]);
    }
  }, [dispatch, contactid, menteeMatches]);

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedMatch(null);
    setMatchDetails({});
    setTopicsData([]);
    currentFormDataRef.current = {}; // Clear ref
  };

  const handleSave = () => {
    // Trigger form submission by finding and clicking the submit button
    // Since we hid the buttons, we need to manually construct and submit the payload
    const matchId = selectedMatch?.ctpt_mentormenteematchesid || selectedMatch?.id;
    if (!matchId) {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Cannot save: Missing record ID",
          code: "",
          display: "show",
          severity: "error",
        },
      });
      return;
    }

    // Get the record widget options to use payloadSubmit
    const options = getRecordWidgetOptions();
    
    // Construct the JsonData object as FormWidget would
    // Use currentFormDataRef if available (it should be updated via updateFormData callback on every change)
    // Otherwise fall back to matchDetails
    const schema = matchDetailsSchema;
    // Prefer currentFormDataRef as it should have the latest values from the form
    const currentFormData = currentFormDataRef.current;
    const formData = (currentFormData && Object.keys(currentFormData).length > 0) ? currentFormData : matchDetails;
    
    console.log("handleSave - currentFormData:", currentFormData);
    console.log("handleSave - matchDetails:", matchDetails);
    console.log("handleSave - using formData:", formData);
    
    const arrayData = [];
    let i = 0;
    for (const key in schema.properties) {
      // Use formData value if available, otherwise fall back to matchDetails
      const value = formData[key] !== undefined ? formData[key] : matchDetails[key];
      arrayData[i] = {
        name: key,
        value: value,
        type: schema.properties[key].type,
      };
      i++;
    }
    
    console.log("handleSave - arrayData before payloadSubmit:", arrayData);
    
    const JsonData = {
      data: arrayData,
      recordtype: "ctpt_mentormenteematcheses",
      id: matchId,
      recordId: matchId,
      contactid: contactid,
    };

    // Use the payloadSubmit function to format the data
    const payload = options.payloadSubmit(JsonData);
    
    console.log("handleSave - final payload:", JSON.stringify(payload, null, 2));
    
    // Submit the form
    const api = options.apiURL;
    if (!api) {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Cannot save: Missing API URL",
          code: "",
          display: "show",
          severity: "error",
        },
      });
      return;
    }

    post(api, payload)
      .then((res) => {
        if (res.data && (res.data.id || res.status === 200 || res.status === 204)) {
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Match details updated successfully",
              code: "",
              display: "show",
              severity: "success",
            },
          });
          // Refresh the data to show updated values in the table
          fetchMenteeMatches();
          // Close the modal
          handleCloseViewModal();
        } else {
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Failed to update match details",
              code: "",
              display: "show",
              severity: "error",
            },
          });
        }
      })
      .catch((error) => {
        console.error("Error saving match details:", error);
        dispatch({
          type: "SET_STATUS",
          payload: {
            message: error.response?.data?.message || "Error saving match details. Please try again.",
            code: "",
            display: "show",
            severity: "error",
          },
        });
      });
  };

  // Schema for record widget (editable view)
  const matchDetailsSchema = {
    type: "object",
    properties: {
      ctpt_mentee: {
        type: "string",
        title: "Mentee",
        description: "Name of the mentee",
      },
      ctpt_approvalstatus: {
        type: "string",
        title: "Approval Status",
        description: "Approval status of the match",
        enum: ["pending", "approved", "rejected"],
        enumNames: ["Pending", "Approved", "Rejected"],
      },
      ctpt_availablitymatches: {
        type: "string",
        title: "Availability Matches",
      },
      ctpt_topicsmatched: {
        type: "boolean",
        title: "Topics Matched",
        description: "Whether topics are matched between mentor and mentee",
      },
      ctpt_completionstatus: {
        type: "string",
        title: "Completion Status",
        description: "Current status of the mentoring relationship",
        enum: ["533910000", "533910001", "533910002"],
        enumNames: ["Not Started", "In Progress", "Complete"],
      },
      ctpt_hourslogged: {
        type: "number",
        title: "Hours Logged",
        description: "Total hours of mentoring sessions completed",
        minimum: 0,
        maximum: 100000000000,
      },
      ctpt_statusnotes: {
        type: "string",
        title: "Status Notes",
        description: "Additional notes about the mentoring relationship status",
      },
    },
    required: ["ctpt_mentee", "ctpt_approvalstatus", "ctpt_completionstatus", "ctpt_hourslogged"],
  };

  const matchDetailsUiSchema = {
    ctpt_mentee: {
      "ui:readonly": true,
      "ui:placeholder": "Enter mentee name",
      "ui:options": {
        inputType: "text",
      },
    },
    ctpt_approvalstatus: {
      "ui:readonly": true,
      "ui:widget": "select",
      "ui:placeholder": "Select approval status",
      "ui:options": {
        label: false,
      },
    },
    ctpt_availablitymatches: {
      "ui:readonly": true,
    },
    ctpt_topicsmatched: {
      "ui:readonly": true,
      "ui:widget": "checkbox",
    },
    ctpt_completionstatus: {
      "ui:readonly": false,
      "ui:widget": "select",
      "ui:placeholder": "Select completion status",
      "ui:options": {
        label: false,
      },
    },
    ctpt_hourslogged: {
      "ui:readonly": false,
      "ui:placeholder": "Enter number of hours (0 to 100000000000)",
      "ui:options": {
        inputType: "number",
        step: 0.5,
        min: 0,
        max: 100000000000,
      },
    },
    ctpt_statusnotes: {
      "ui:readonly": false,
      "ui:widget": "textarea",
      "ui:placeholder": "Enter notes about the mentoring relationship status",
      "ui:options": {
        rows: 4,
      },
    },
  };

  // Columns for topics list widget
  const topicsColumns = [
    {
      field: "topicName",
      flex: 1,
      headerName: "Topic",
      headerClassName: "super-app-theme--header",
    },
    // {
    //   field: "description",
    //   flex: 1.5,
    //   headerName: "Description",
    //   headerClassName: "super-app-theme--header",
    // },
  ];

  // Configuration for topics list widget - will be updated with current topicsData when used
  const getTopicsListConfiguration = () => ({
    apiURL: "",
    apiUrlDel: "",
    columns: topicsColumns,
    getlistPayload: {},
    deleteListPayload: {},
    listHeading: "", // Remove heading
    nonRecord: false,
    localDataStore: false,
    hideControls: true,
    hideAdd: true,
    hasAggregate: false,
    staticTable: true,
    staticRow: topicsData,
    formatStaticRow: false,
    hideDelete: true,
    hideSearch: true,
    enableLocalSearch: false,
    hideFooterPagination: true,
    pageSize: 10,
    getRowId: (row) => row.id || row.ctpt_topicsid,
  });

  // Memoize payloadSubmit function to prevent recreation on every render
  const payloadSubmitFn = useCallback((JsonData) => {
    // Custom payload submit function to format the data correctly
    const matchId = selectedMatch?.ctpt_mentormenteematchesid || selectedMatch?.id;
    const data = JsonData.data || [];
    
    // Include all editable fields (excluding readonly fields: mentee, approval status, topics matched, and availability matches)
    const editableFields = [
      "ctpt_completionstatus", 
      "ctpt_hourslogged", 
      "ctpt_statusnotes"
    ];
    const filteredData = data.filter(field => editableFields.includes(field.name));
    
    return {
      id: matchId,
      recordtype: "ctpt_mentormenteematcheses",
      data: filteredData.map(field => {
        // Determine the correct type and value for each field
        let fieldType = field.type;
        let fieldValue = field.value;
        
        if (field.name === "ctpt_mentee") {
          fieldType = "string";
          fieldValue = fieldValue !== null && fieldValue !== undefined 
            ? String(fieldValue).trim() 
            : "";
        } else if (field.name === "ctpt_approvalstatus") {
          // Approval status is readonly and should be filtered out, but handle it just in case
          // Convert status string to option set value
          // 533910000 = Approved, 533910001 = Rejected
          fieldType = "optionsetvalue";
          const statusLower = String(fieldValue).toLowerCase();
          if (statusLower === "approved" || statusLower === "approve") {
            fieldValue = 533910000; // Approved
          } else if (statusLower === "rejected" || statusLower === "reject") {
            fieldValue = 533910001; // Rejected
          } else {
            // For Pending or unknown status, try to parse existing numeric value
            const numValue = Number(fieldValue);
            if (!isNaN(numValue)) {
              fieldValue = numValue;
            } else {
              // If we can't determine the value, skip this field
              return null;
            }
          }
        } else if (field.name === "ctpt_completionstatus") {
          // Completion status is an option set value
          fieldType = "optionsetvalue";
          const statusValue = String(fieldValue).trim();
          // Map to option set values: 533910000 = Not Started, 533910001 = In Progress, 533910002 = Complete
          if (statusValue === "533910000" || statusValue === "533910001" || statusValue === "533910002") {
            fieldValue = Number(statusValue);
          } else {
            // Default to Not Started if invalid
            fieldValue = 533910000;
          }
        } else if (field.name === "ctpt_hourslogged") {
          fieldType = "decimal";
          // Ensure the value is a number and within range
          const numValue = Number(fieldValue);
          if (isNaN(numValue) || numValue < 0) {
            fieldValue = 0;
          } else if (numValue > 100000000000) {
            fieldValue = 100000000000;
          } else {
            fieldValue = numValue;
          }
        } else if (field.name === "ctpt_statusnotes") {
          fieldType = "string";
          // Ensure the value is a string
          fieldValue = fieldValue !== null && fieldValue !== undefined 
            ? String(fieldValue) 
            : "";
        } else if (field.name === "ctpt_topicsmatched") {
          fieldType = "boolean";
          // Ensure the value is a boolean
          fieldValue = fieldValue === true || fieldValue === "true" || fieldValue === 1 || fieldValue === "1";
        }
        
        return {
          name: field.name,
          value: fieldValue,
          type: fieldType,
        };
      }).filter(field => field !== null), // Filter out any null fields
    };
  }, [selectedMatch]);

  // Options for record widget - memoized to prevent unnecessary re-renders
  // Note: We use matchDetails for formData initially, and FormWidget manages its own state
  // currentFormData is only used when saving, not for rendering
  const recordWidgetOptions = useMemo(() => {
    const matchId = selectedMatch?.ctpt_mentormenteematchesid || selectedMatch?.id;
    
    return {
      widgetType: "update",
      formSchema: matchDetailsSchema,
      uiSchema: matchDetailsUiSchema,
      entityType: "ctpt_mentormenteematcheses",
      formData: matchDetails, // Use initial data - FormWidget manages its own state
      initialFormData: matchDetails,
      button1Label: "", // Hide buttons - we'll add custom buttons below topics
      button2Label: "", // Hide buttons - we'll add custom buttons below topics
      button2GoBack: false,
      hasModel: true,
      contactId: contactid,
      apiURL: matchId ? `${config.teleportal.submitForm.Update}` : "",
      recordId: matchId || "",
      historyUrl: "",
      payloadSubmit: payloadSubmitFn,
    };
  }, [selectedMatch, matchDetails, contactid, payloadSubmitFn]);

  // Keep getRecordWidgetOptions as a function for backward compatibility with handleSave
  const getRecordWidgetOptions = () => recordWidgetOptions;

  return (
    <ThemeProvider theme={globalTheme}>
      <Heading>
        <h2>Mentoring - Review Your Matches</h2>
      </Heading>
      <Content>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Typography variant="h5" component="h3" gutterBottom className={classes.title}>
              Mentee Matches
            </Typography>
            <Typography variant="body1" gutterBottom style={{ marginBottom: "1rem" }}>
              Review mentor/mentee matches and take the next action.
            </Typography>
            {loading ? (
              <Box className={classes.loadingContainer}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} aria-label="mentee matches table">
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell>Mentee</TableCell>
                      <TableCell>Availability Matches</TableCell>
                      <TableCell>Approved</TableCell>
                      <TableCell>Created On</TableCell>
                      <TableCell align="center" className={classes.actionCell}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {menteeMatches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No mentee matches found
                        </TableCell>
                      </TableRow>
                    ) : (
                      menteeMatches.map((match) => {
                        const approvalStatusText = formatApprovalStatus(match, "ctpt_approvalstatus");
                        const approvedYesNo = getApprovedYesNo(match);
                        const showApproveButton = isPendingApprovalStatus(match);

                        return (
                        <TableRow key={match.ctpt_mentormenteematchesid || match.id} hover>
                          <TableCell>{getMenteeName(match)}</TableCell>
                          <TableCell>{formatAvailabilityMatches(match, "ctpt_availablitymatches")}</TableCell>
                          <TableCell>
                            <span className={getStatusClass(approvalStatusText)}>
                              {approvedYesNo}
                            </span>
                          </TableCell>
                          <TableCell>{formatDateForDisplay(match)}</TableCell>
                          <TableCell align="center" className={classes.actionCell}>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              className={classes.actionButton}
                              onClick={() => handleView(match)}
                            >
                              View
                            </Button>
                            {showApproveButton && (
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                className={classes.actionButton}
                                onClick={() => handleApprove(match)}
                              >
                                Approve
                              </Button>
                            )}
                            <Button
                              variant="outlined"
                              color="secondary"
                              size="small"
                              className={classes.actionButton}
                              onClick={() => handleReject(match)}
                            >
                              Reject
                            </Button>
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </Content>
      
      {/* View Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="lg"
        fullWidth
        aria-labelledby="view-match-dialog-title"
        classes={{
          paper: classes.modalPaper,
        }}
      >
        <DialogTitle id="view-match-dialog-title">
          Match Details
          <IconButton
            aria-label="close"
            className={classes.modalClose}
            onClick={handleCloseViewModal}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          ref={dialogContentRef}
          className={classes.modalContent}
        >
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormWidget
                  key={`match-${selectedMatch?.ctpt_mentormenteematchesid || selectedMatch?.id || 'new'}`}
                  options={recordWidgetOptions}
                  updateFormData={handleUpdateFormData}
                  successResponse={(res) => {
                    // This won't be called since we're handling submission manually
                    // But keeping it for compatibility
                  }}
                  handleModalClose={handleCloseViewModal}
                />
              </Grid>
              <Grid item xs={12}>
                {topicsData.length > 0 ? (
                  <ListWidget 
                    key={`topics-${topicsData.length}`}
                    options={getTopicsListConfiguration()} 
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary" style={{ padding: "1rem" }}>
                    No topics found for this match.
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "1rem 1.5rem", justifyContent: "flex-start" }}>
          <Button
            onClick={handleCloseViewModal}
            color="secondary"
            variant="outlined"
            size="medium"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            size="medium"
            disabled={loadingDetails}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default MentoringReviewRequests;


